import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Badge } from './ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog'
import { toast } from 'react-hot-toast'
import { Key, Plus, Trash2, Copy, AlertTriangle, Check, Sparkles } from 'lucide-react'

const ApiKeyManager = ({ user }) => {
  const [keys, setKeys] = useState([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [newKeyName, setNewKeyName] = useState('')
  const [newKeyDialogOpen, setNewKeyDialogOpen] = useState(false)
  const [generatedKey, setGeneratedKey] = useState(null)
  const [showKeyDialogOpen, setShowKeyDialogOpen] = useState(false)

  const isPro = ['pro', 'business'].includes(user?.subscription_tier)

  useEffect(() => { loadKeys() }, [])

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json',
  })

  const loadKeys = async () => {
    try {
      const res = await fetch('/api/api-keys/list', { headers: authHeaders() })
      if (res.ok) setKeys(await res.json())
    } catch (e) {
      toast.error('Fehler beim Laden der Keys')
    } finally {
      setLoading(false)
    }
  }

  const generateKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Bitte einen Namen eingeben')
      return
    }
    setCreating(true)
    try {
      const res = await fetch('/api/api-keys/generate', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ name: newKeyName }),
      })
      const data = await res.json()
      if (!res.ok) {
        toast.error(data.detail || 'Fehler')
        return
      }
      setGeneratedKey(data.api_key)
      setNewKeyDialogOpen(false)
      setShowKeyDialogOpen(true)
      setNewKeyName('')
      loadKeys()
    } catch (e) {
      toast.error('Netzwerkfehler')
    } finally {
      setCreating(false)
    }
  }

  const revokeKey = async (id) => {
    if (!confirm('Diesen API-Key wirklich widerrufen? Bestehende Integrationen funktionieren danach nicht mehr.')) return
    try {
      const res = await fetch(`/api/api-keys/${id}`, { method: 'DELETE', headers: authHeaders() })
      if (res.ok) {
        toast.success('Key widerrufen')
        loadKeys()
      }
    } catch (e) { toast.error('Fehler') }
  }

  const copyKey = (key) => {
    navigator.clipboard.writeText(key)
    toast.success('Key kopiert')
  }

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'Nie'

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Key className="h-6 w-6 text-blue-600" />
            API-Schlüssel
          </h1>
          <p className="text-slate-500 mt-1">
            Für Claude Code MCP-Plugin, Zapier-Integration und externe Automationen
          </p>
        </div>
        <Button
          onClick={() => isPro ? setNewKeyDialogOpen(true) : toast.error('API-Zugriff erfordert Pro-Plan')}
          className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
          disabled={!isPro}
        >
          <Plus className="h-4 w-4" />
          Neuer API-Schlüssel
        </Button>
      </div>

      {!isPro && (
        <Card className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="py-4 flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-amber-900">Upgrade auf Pro um API-Zugriff zu aktivieren</p>
              <p className="text-sm text-amber-700">Mit Pro kannst du das offfer MCP-Plugin in Claude Code nutzen und Workflows automatisieren.</p>
            </div>
            <Badge className="bg-amber-200 text-amber-900 hover:bg-amber-200">Pro Feature</Badge>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Deine API-Schlüssel</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-8 text-slate-400">Lade...</p>
          ) : keys.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Key className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>Noch keine API-Schlüssel erstellt</p>
              {isPro && <p className="text-sm mt-1">Klick auf "Neuer API-Schlüssel" um zu starten</p>}
            </div>
          ) : (
            <div className="space-y-2">
              {keys.map((k) => (
                <div key={k.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-900">{k.name}</p>
                      {k.is_active ? (
                        <Badge variant="outline" className="text-green-600 border-green-200">Aktiv</Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-400">Widerrufen</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 font-mono">{k.key_prefix}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      Erstellt: {formatDate(k.created_at)} · Zuletzt benutzt: {formatDate(k.last_used_at)}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => revokeKey(k.id)} className="text-red-600 hover:bg-red-50">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* MCP Setup Instructions */}
      {isPro && keys.length > 0 && (
        <Card className="mt-6 bg-slate-50">
          <CardHeader>
            <CardTitle className="text-base">Claude Code MCP-Plugin einrichten</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-3">
              Füge folgendes zu deiner <code className="text-xs bg-slate-200 px-1 rounded">~/.claude/settings.json</code> hinzu:
            </p>
            <pre className="text-xs bg-slate-900 text-slate-100 p-3 rounded overflow-x-auto">
{`{
  "mcpServers": {
    "offfer": {
      "command": "npx",
      "args": ["-y", "@offfer/mcp-server"],
      "env": {
        "BIZQUOTE_API_KEY": "<dein-api-key>",
        "BIZQUOTE_API_URL": "${window.location.origin}/api/v1"
      }
    }
  }
}`}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* Create Dialog */}
      <Dialog open={newKeyDialogOpen} onOpenChange={setNewKeyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Neuen API-Schlüssel erstellen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Name (für deine Übersicht)</label>
              <Input value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} placeholder="z.B. Claude Code Plugin, Zapier" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setNewKeyDialogOpen(false)}>Abbrechen</Button>
              <Button onClick={generateKey} disabled={creating} className="bg-blue-600 hover:bg-blue-700 text-white">
                {creating ? 'Erstelle...' : 'Erstellen'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Show Key Once Dialog */}
      <Dialog open={showKeyDialogOpen} onOpenChange={setShowKeyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-600" />
              API-Schlüssel erstellt
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <p className="font-semibold">Wichtig: Jetzt sicher speichern!</p>
                <p>Der vollständige Key wird nur dieses eine Mal angezeigt.</p>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Dein API-Schlüssel</label>
              <div className="flex gap-2">
                <Input value={generatedKey || ''} readOnly className="font-mono text-xs" />
                <Button onClick={() => copyKey(generatedKey)} className="bg-blue-600 hover:bg-blue-700 text-white gap-2">
                  <Copy className="h-4 w-4" /> Kopieren
                </Button>
              </div>
            </div>
            <Button onClick={() => setShowKeyDialogOpen(false)} className="w-full">
              Verstanden, ich habe den Key gespeichert
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default ApiKeyManager
